import { BRAND, BRAND_LIGHT, pageTitle, WHITE, LINE, MUTED, INK, TEXT, ROW_LINE, NEUTRAL_BG } from "./constants";
import { useIsMobile } from "./lib/responsive";
import { HelpButton } from "./shared";

// Methodology: es el producto, no texto de marketing (ver CLAUDE.md, "LA
// METODOLOGÍA"). Tres bloques en el lenguaje de Home y Evaluación:
//   1. La base (3 orígenes) como fila de tarjetas iguales
//   2. El recorrido: 0 Mapping → 1 Cualificación → 2 Evaluación → 3 Referencias,
//      cada paso con qué pasa, quién lo hace y qué recibes
//   3. Los 4 vectores con su escala 1-5 y la regla de la justificación escrita
const MethodologyView = ({ t, lang, onShowOnboarding }) => {
  const es = lang === "es";
  const isMobile = useIsMobile();

  const card = { background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 14 };
  const lbl = { fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4 };
  const h2 = { fontSize: 16, fontWeight: 800, color: INK, margin: "0 0 4px" };
  const sub = { fontSize: 13, color: MUTED, margin: "0 0 16px", lineHeight: 1.6 };
  const cardTitle = { fontSize: 15, fontWeight: 800, color: INK, margin: 0 };
  const body = { fontSize: 13, color: TEXT, lineHeight: 1.6, margin: 0 };

  const origins = [
    {
      title: es ? "Investigación académica" : "Academic research",
      desc: es
        ? "Estudios sobre contratación de las principales escuelas y empresas."
        : "Hiring research from leading schools and companies.",
    },
    {
      title: es ? "Top headhunters globales" : "Top global headhunters",
      desc: es
        ? "Las técnicas de las firmas de executive search de referencia."
        : "The techniques of the reference executive search firms.",
    },
    {
      title: es ? "Experiencia del equipo Rutment" : "Rutment team experience",
      desc: es
        ? "Refinada en procesos reales para hacerla ágil y efectiva."
        : "Refined in real processes to make it fast and effective.",
    },
  ];

  // El mapping es el paso 0: decide a quién se contacta, no cómo se evalúa.
  // Por eso lleva su etiqueta "antes de contactar" en vez de ser una fase más.
  const steps = [
    {
      n: 0,
      title: "Mapping",
      when: es ? "Antes de contactar a nadie" : "Before contacting anyone",
      what: es
        ? "Te proponemos empresas objetivo y perfiles anónimos. Validas, vetas con un motivo o comentas."
        : "We propose target companies and anonymised profiles. You validate, veto with a reason or comment.",
      who: es ? "Rutment propone · tú apruebas" : "Rutment proposes · you approve",
      get: es
        ? "Un mapa de mercado aprobado. Los nombres no aparecen hasta que cada persona acepta."
        : "An approved market map. Names stay hidden until each person agrees.",
    },
    {
      n: 1,
      title: es ? "Cualificación" : "Qualification",
      when: es ? "Filtra" : "Filters",
      what: es
        ? "Cada candidato se puntúa en los 4 vectores con la evidencia de su intake."
        : "Every candidate is scored on the 4 vectors using the evidence from their intake.",
      who: "Rutment",
      get: es
        ? "Solo los que encajan, cada nota con su porqué."
        : "Only those who fit, each score with its why.",
    },
    {
      n: 2,
      title: es ? "Evaluación" : "Evaluation",
      when: es ? "Entrevistas" : "Interviews",
      what: es
        ? "Tu equipo entrevista con el Interviewer guide: preguntas calibradas contra las brechas de la cualificación."
        : "Your team interviews with the Interviewer guide: questions calibrated against the gaps qualification found.",
      who: es ? "Tu equipo, con la guía de Rutment" : "Your team, with Rutment's guide",
      get: es
        ? "Notas comparables, entreviste quien entreviste."
        : "Comparable scores, whoever interviews.",
    },
    {
      n: 3,
      title: es ? "Referencias" : "References",
      when: es ? "Antes de decidir" : "Before deciding",
      what: es
        ? "Verificación con quienes han trabajado con esa persona."
        : "Verification with people who have worked with them.",
      who: "Rutment",
      get: es
        ? "La confirmación final, lo que casi nadie hace bien."
        : "The final check, the step almost nobody does well.",
    },
  ];

  const vectors = [
    {
      title: es ? "Fit técnico" : "Technical fit",
      desc: es
        ? "Si ha hecho este rol antes y tiene el conocimiento, el sector y la red que pide."
        : "Whether they have done this role and have the knowledge, sector and network it needs.",
      sample: 5,
    },
    {
      title: "Soft skills",
      desc: es
        ? "Las habilidades interpersonales que pide el puesto para su seniority."
        : "The interpersonal skills the role requires at this seniority.",
      sample: 4,
    },
    {
      title: es ? "Fit cultural" : "Cultural fit",
      desc: es
        ? "Si sus valores y forma de trabajar encajan con el equipo y el momento de la empresa."
        : "Whether their values and way of working fit the team and the company's moment.",
      sample: 4,
    },
    {
      title: es ? "Potencial de crecimiento" : "Growth potential",
      desc: es
        ? "Su capacidad de adaptarse, asumir más y crecer con la empresa."
        : "Their ability to adapt, take on more and grow with the company.",
      sample: 3,
    },
  ];

  const scale = [
    // "Supera lo que pide el puesto" sonaba a sobrecualificado, que en senior
    // es un riesgo, no una virtud. Un 5 es encaje perfecto.
    [5, es ? "Encaje perfecto" : "Perfect fit", es ? "Exactamente lo que necesita." : "Exactly what it needs."],
    [4, es ? "Sólido" : "Strong", es ? "Cumple con holgura." : "Comfortably meets it."],
    [3, es ? "Suficiente" : "Adequate", es ? "Cumple, con matices." : "Meets it, with caveats."],
    [2, es ? "Justo" : "Below", es ? "Hay una brecha real." : "There's a real gap."],
    [1, es ? "No encaja" : "Not a fit", es ? "No cumple aquí." : "Doesn't meet it."],
  ];

  return (
    <div style={{ padding: isMobile ? 16 : 32, maxWidth: 1180 }}>
      <div style={{ marginBottom: isMobile ? 16 : 20 }}>
        {/* El «?» abre el recorrido de Cómo funciona: desde el 31/10 es su
            único acceso fuera de la primera vez (el menú ya no lo repite) */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <h1 style={pageTitle(isMobile, 0)}>
            {es ? "Metodología" : "Methodology"}
          </h1>
          {onShowOnboarding && <HelpButton onClick={onShowOnboarding} label={es ? "Cómo funciona" : "How it works"} />}
        </div>
        <p style={{ color: MUTED, fontSize: 13, margin: 0, lineHeight: 1.6 }}>
          {es
            ? "Un recorrido en cuatro pasos y la misma vara de medir para todos los candidatos."
            : "A four-step journey and the same yardstick for every candidate."}
        </p>
      </div>

      {/* ── La base ── */}
      <div style={{ ...lbl, marginBottom: 10 }}>{es ? "La base" : "Foundation"}</div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(auto-fit, minmax(220px, 1fr))", gap: isMobile ? 12 : 14, marginBottom: isMobile ? 28 : 36 }}>
        {origins.map(o => (
          <div key={o.title} style={{ ...card, padding: isMobile ? "16px" : "18px 20px" }}>
            <h3 style={{ ...cardTitle, marginBottom: 6 }}>{o.title}</h3>
            <p style={body}>{o.desc}</p>
          </div>
        ))}
      </div>

      {/* ── El recorrido ── */}
      <h2 style={h2}>{es ? "El recorrido" : "The journey"}</h2>
      <p style={sub}>
        {es
          ? "Cada paso responde una pregunta distinta, y ninguno sustituye al anterior."
          : "Each step answers a different question, and none replaces the one before."}
      </p>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(auto-fit, minmax(220px, 1fr))", gap: isMobile ? 12 : 14, marginBottom: isMobile ? 28 : 36 }}>
        {steps.map((s, i) => (
          <div key={s.n} style={{ display: "flex", flexDirection: "column" }}>
            {/* Marcador + línea hacia el siguiente paso: el recorrido se ve, no se lee */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ width: 26, height: 26, borderRadius: "50%", background: BRAND_LIGHT, color: BRAND, fontSize: 13, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{s.n}</span>
              <span style={lbl}>{s.when}</span>
              {!isMobile && i < steps.length - 1 && <span style={{ flex: 1, height: 2, borderRadius: 2, background: BRAND_LIGHT, minWidth: 12 }} />}
            </div>
            <div style={{ ...card, padding: isMobile ? "16px" : "18px 20px", flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <h3 style={{ ...cardTitle, marginBottom: 6 }}>{s.title}</h3>
                <p style={body}>{s.what}</p>
              </div>
              <div style={{ marginTop: "auto", paddingTop: 12, borderTop: `1px solid ${ROW_LINE}`, display: "flex", flexDirection: "column", gap: 8 }}>
                <div>
                  <div style={{ ...lbl, marginBottom: 2 }}>{es ? "Quién" : "Who"}</div>
                  <div style={{ fontSize: 13, color: INK, fontWeight: 600 }}>{s.who}</div>
                </div>
                <div>
                  <div style={{ ...lbl, marginBottom: 2 }}>{es ? "Qué recibes" : "What you get"}</div>
                  <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.5 }}>{s.get}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Los 4 vectores ── */}
      <h2 style={h2}>{es ? "Los 4 vectores" : "The 4 vectors"}</h2>
      <p style={sub}>
        {es
          ? "No solo si puede hacer el trabajo, sino si encajará y crecerá contigo. Se leen juntos: la forma dice más que la media."
          : "Not just whether they can do the job, but whether they'll fit and grow with you. Read together: the shape says more than the average."}
      </p>
      <div style={{ ...card, padding: isMobile ? "4px 16px 16px" : "6px 22px 20px" }}>
        {vectors.map((v, i) => (
          <div key={v.title} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px 24px", padding: "14px 0", borderBottom: i < vectors.length - 1 ? `1px solid ${ROW_LINE}` : "none" }}>
            <div style={{ flex: isMobile ? "1 1 100%" : "1 1 280px", minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: INK, marginBottom: 2 }}>{v.title}</div>
              <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.5 }}>{v.desc}</div>
            </div>
            <div style={{ flex: isMobile ? "1 1 100%" : "0 1 200px", minWidth: isMobile ? 0 : 160 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 5 }}>
                <span style={{ color: MUTED }}>{es ? "Ejemplo" : "Example"}</span>
                <span style={{ color: INK, fontWeight: 700 }}>{v.sample}<span style={{ color: MUTED, fontWeight: 500 }}>/5</span></span>
              </div>
              <div style={{ background: NEUTRAL_BG, borderRadius: 4, height: 5 }}>
                <div style={{ background: BRAND, borderRadius: 4, height: 5, width: `${(v.sample / 5) * 100}%` }} />
              </div>
            </div>
          </div>
        ))}

        {/* La escala: es lo que hace auditable el criterio */}
        <div style={{ borderTop: `1px solid ${ROW_LINE}`, paddingTop: 18, marginTop: 4 }}>
          <div style={{ ...lbl, marginBottom: 12 }}>{es ? "Escala 1-5" : "1-5 scale"}</div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(auto-fit, minmax(130px, 1fr))", gap: 14, marginBottom: 16 }}>
            {scale.map(([n, label, desc]) => (
              <div key={n}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 2 }}>
                  <span style={{ fontSize: 16, fontWeight: 800, color: INK }}>{n}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: INK }}>{label}</span>
                </div>
                <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.5 }}>{desc}</div>
              </div>
            ))}
          </div>
          <p style={body}>
            {es
              ? "Cada nota lleva una justificación escrita: sin el porqué no vale nada. Un 5 es encaje perfecto, nunca \"supera lo que pide el puesto\": en senior, sobrecualificado es un riesgo."
              : "Every score carries a written justification: without the why it is worth nothing. A 5 is a perfect fit, never \"exceeds the role\": at senior level, overqualified is a risk."}
          </p>
        </div>
      </div>
    </div>
  );
};

export default MethodologyView;
